import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import authService, { AdminUser, Session } from '../services/authService';

// AdminUser interface is now imported from authService

interface AuthContextType {
  isAuthenticated: boolean;
  user: AdminUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: (logoutAll?: boolean) => Promise<void>;
  forceLogout: () => void;
  isAdmin: () => boolean;
  isSystemAdmin: () => boolean;
  passwordChangeRequired: boolean;
  forgotPassword: (email: string) => Promise<{ message: string }>;
  resetPassword: (token: string, newPassword: string) => Promise<{ message: string }>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<{ message: string }>;
  getSessions: () => Promise<Session[]>;
  terminateSession: (sessionId: string) => Promise<{ message: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Global logout handler for use outside React components (e.g., in api.ts)
let globalForceLogout: (() => void) | null = null;

export const setGlobalLogoutHandler = (handler: () => void) => {
  globalForceLogout = handler;
};

export const getGlobalLogoutHandler = () => globalForceLogout;

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);

  // Force logout without calling API - used when token refresh fails
  const forceLogout = () => {
    // Clear all auth-related data from localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('user');
    localStorage.removeItem('password_change_required');
    
    // Update React state
    setUser(null);
    setPasswordChangeRequired(false);
    setLoading(false);
  };

  // Set the global logout handler for use in api.ts
  React.useEffect(() => {
    setGlobalLogoutHandler(forceLogout);
    
    // Cleanup on unmount
    return () => {
      setGlobalLogoutHandler(() => {});
    };
  }, []);

  useEffect(() => {
    // Check if user is already logged in and refresh token if needed
    const checkAuth = async () => {
      try {
        const currentUser = authService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          
          // Load password change required flag from localStorage
          const passwordChangeRequiredFlag = localStorage.getItem('password_change_required');
          setPasswordChangeRequired(passwordChangeRequiredFlag === 'true');
          
          // Check if token needs refresh
          const tokenValid = await authService.checkAndRefreshToken();
          if (!tokenValid) {
            // Token refresh failed, force logout
            forceLogout();
            return;
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        // If auth check fails, force logout to clear any inconsistent state
        forceLogout();
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const response = await authService.login({ email, password });
      setUser(response.user);
      
      // Set the password change required flag
      setPasswordChangeRequired(response.passwordChangeRequired);
      console.log('Password change required:', response.passwordChangeRequired);
      
      // Store the flag in localStorage to persist across page refreshes
      if (response.passwordChangeRequired) {
        localStorage.setItem('password_change_required', 'true');
      } else {
        localStorage.removeItem('password_change_required');
      }
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async (logoutAll: boolean = false) => {
    await authService.logout(logoutAll);
    setUser(null);
    setPasswordChangeRequired(false);
    
    // Clear password change required flag from localStorage
    localStorage.removeItem('password_change_required');
  };

  const isAdmin = () => {
    return authService.isAdmin();
  };
  
  const isSystemAdmin = () => {
    return authService.isSystemAdmin();
  };

  // Password management functions
  const forgotPassword = async (email: string) => {
    return await authService.forgotPassword(email);
  };

  const resetPassword = async (token: string, newPassword: string) => {
    return await authService.resetPassword(token, newPassword);
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!user) throw new Error('User not authenticated');
    const result = await authService.changePassword(user._id, currentPassword, newPassword);
    
    // If password was changed successfully and it was required, clear the flag
    if (passwordChangeRequired) {
      setPasswordChangeRequired(false);
      localStorage.removeItem('password_change_required');
      
      // Update the user object to reflect that password change is no longer required
      if (user) {
        const updatedUser = { ...user, passwordChangeRequired: false };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
    }
    
    return result;
  };

  // Session management functions
  const getSessions = async () => {
    return await authService.getSessions();
  };

  const terminateSession = async (sessionId: string) => {
    return await authService.terminateSession(sessionId);
  };

  const value = {
    isAuthenticated: !!user,
    user,
    loading,
    login,
    logout,
    forceLogout,
    isAdmin,
    isSystemAdmin,
    passwordChangeRequired,
    forgotPassword,
    resetPassword,
    changePassword,
    getSessions,
    terminateSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
